import React, {useState} from 'react'
import {Box, Link, Stack, Typography} from '@mui/material'
import WbTextField from '../../components/common/WbTextField'
import WbOutlineButton from '../../components/common/WbOutlineButton'
import {
  ATTRIBUTE_KEYS,
  PARTICIPANT_TYPES,
} from '../../utils/skipLogic/conditions'
import {personaInputs} from '../../utils/skipLogic/preview'

// Chooses who the preview runs as, before the survey starts. It asks for the
// participant type when the survey has more than one audience - so the author can
// preview each audience's version - or when a rule references the
// participant type, plus any attributes a rule references; a generic respondent
// (Skip) leaves participant/attribute conditions "unknown".
// Calls onStart(persona) with { audience?, participantType?, attributes }.
export default function PersonaPicker({items, audiencePersonas = [], onStart}) {
  const {usesParticipantType, attributeKeys} = personaInputs(items)
  const multiAudience = audiencePersonas.length > 1
  const firstAudience = audiencePersonas[0]
  const [participantType, setParticipantType] = useState(
    multiAudience ? firstAudience?.participantType || '' : ''
  )
  const [attributes, setAttributes] = useState({})

  // Build the persona the runner gets. On a multi-audience survey the chosen type
  // also picks which audience's text is rendered; otherwise the render audience
  // comes from the one audience the container already passed.
  const asPersona = (type, attrs) => {
    const match = audiencePersonas.find(p => p.participantType === type)
    return {
      audience: multiAudience
        ? match?.audience ?? firstAudience?.audience
        : undefined,
      participantType: type || undefined,
      attributes: {...attrs},
    }
  }

  // Nothing makes the persona matter: one audience and no participant/attribute
  // rules. Preview straight away.
  if (!multiAudience && !usesParticipantType && attributeKeys.length === 0) {
    return (
      <Box className="persona-picker" sx={{p: 3, maxWidth: 420}}>
        <Typography variant="h6" sx={{mb: 1}}>
          Preview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          This survey has no participant-based rules, so you can preview as any
          respondent.
        </Typography>
        <WbOutlineButton
          CustomOutlineButtonText="Start preview"
          onClick={() => onStart({})}
        />
      </Box>
    )
  }

  const setAttr = (key, value) => setAttributes(a => ({...a, [key]: value}))
  const start = () => onStart(asPersona(participantType, attributes))

  // For a multi-audience survey the type options are the survey's own audiences
  // and one must be chosen (there is no "unknown" - the preview has to render
  // someone). When only a rule needs it, keep the full participant list with an
  // unknown option.
  const typeOptions = multiAudience
    ? audiencePersonas.map(p => ({value: p.participantType, label: p.label}))
    : PARTICIPANT_TYPES
  const showType = multiAudience || usesParticipantType

  return (
    <Box className="persona-picker" sx={{p: 3, maxWidth: 420}}>
      <Typography variant="h6" sx={{mb: 1}}>
        Preview as…
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
        {multiAudience
          ? 'This survey has more than one audience. Choose who to preview as so you see their version and skip logic routes the way it would for them.'
          : 'Choose who to preview as, so skip logic routes the way it would for them.'}
      </Typography>
      <Stack spacing={2}>
        {showType && (
          <WbTextField
            select
            SelectProps={{native: true}}
            InputLabelProps={{shrink: true}}
            label="Participant type"
            value={participantType}
            onChange={e => setParticipantType(e.target.value)}
          >
            {!multiAudience && <option value="">(any / unknown)</option>}
            {typeOptions.map(p => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </WbTextField>
        )}

        {attributeKeys.map(key => {
          const spec = ATTRIBUTE_KEYS.find(k => k.key === key)
          const label = spec?.label || key
          if (spec && !spec.free && Array.isArray(spec.values)) {
            return (
              <WbTextField
                key={key}
                select
                SelectProps={{native: true}}
                InputLabelProps={{shrink: true}}
                label={label}
                value={attributes[key] || ''}
                onChange={e => setAttr(key, e.target.value)}
              >
                <option value="">(unknown)</option>
                {spec.values.map(({value, label: valueLabel}) => (
                  <option key={value} value={value}>
                    {valueLabel}
                  </option>
                ))}
              </WbTextField>
            )
          }
          return (
            <WbTextField
              key={key}
              label={label}
              value={attributes[key] || ''}
              onChange={e => setAttr(key, e.target.value)}
            />
          )
        })}
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{mt: 3}}>
        <WbOutlineButton CustomOutlineButtonText="Start preview" onClick={start} />
        {!multiAudience && (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => onStart({})}
          >
            Skip — preview as a generic respondent
          </Link>
        )}
      </Stack>
    </Box>
  )
}
